import React, { useState, useEffect, useRef } from 'react';
import {
  Plus, Filter, Eye, Pencil, Trash2,
  X, Upload, IndianRupee, FileText, Image,
  ChevronLeft, ChevronRight, RefreshCw, CircleDollarSign
} from 'lucide-react';
import { LoadingSpinner, Button } from '../../UI';
import ProfitSummary from './ProfitSummary';
import {
  C, FONT_UI, FONT_DATA, Mono, Card, Pill, PageHeader, PrimaryButton, GhostButton,
  IconButton, Toolbar, Table, TH, TD, EmptyState, Banner, fmtDate,
} from './theme';
import { useApi, useAsyncAction } from '../../../hooks';
import { profitAPI, clientAPI } from '../../../services';
import { useAuth } from '../../../context/AuthContext';

const CATEGORIES = [
  { value: 'client_payment', label: 'Client Payment' },
  { value: 'project_fee', label: 'Project Fee' },
  { value: 'subscription', label: 'Subscription' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'commission', label: 'Commission' },
  { value: 'interest', label: 'Interest' },
  { value: 'refund', label: 'Refund' },
  { value: 'other', label: 'Other' }
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: '💵' },
  { value: 'upi', label: 'UPI', icon: '📱' },
  { value: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
  { value: 'credit_card', label: 'Credit Card', icon: '💳' },
  { value: 'debit_card', label: 'Debit Card', icon: '💳' },
  { value: 'cheque', label: 'Cheque', icon: '📝' },
  { value: 'other', label: 'Other', icon: '📄' }
];

const STATUS_OPTIONS = [
  { value: 'received', label: 'Received', color: 'bg-green-100 text-green-700' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' }
];

// Maps profit-record statuses onto the shared Pill tones.
const PROFIT_STATUS_TONE = { received: 'success', pending: 'warning', cancelled: 'danger' };

const ProfitsPage = () => {
  const { user } = useAuth();
  const canManage = (action) => user?.role !== 'platform_staff' || user?.platformPermissions?.profits?.[action] === true;
  const [profits, setProfits] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    paymentMethod: '',
    status: '',
    startDate: '',
    endDate: '',
    client: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProfit, setEditingProfit] = useState(null);
  const [viewingProfit, setViewingProfit] = useState(null);
  
  
  // Clients for dropdown
  const { data: clientsData } = useApi(clientAPI.getClients, []);
  const clients = clientsData?.clients || [];
  
  // Form state
  const [form, setForm] = useState({
    title: '',
    description: '',
    amount: '',
    category: 'client_payment',
    paymentMethod: 'bank_transfer',
    upiReferenceId: '',
    bankTransactionId: '',
    chequeNumber: '',
    date: new Date().toISOString().split('T')[0],
    sourceName: '',
    sourceContact: '',
    client: '',
    status: 'received',
    notes: '',
    tags: ''
  });
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const fileInputRef = useRef(null);
  
  const { execute, loading: actionLoading } = useAsyncAction();

  useEffect(() => {
    fetchProfits();
  }, [pagination.page, filters]);

  const fetchProfits = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      };
      const response = await profitAPI.getProfits(params);
      if (response.status === 'success') {
        setProfits(response.data?.profits || []);
        setPagination(response.data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
      } else {
        setProfits([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch profit records');
      setProfits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setReceiptPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setReceiptPreview(null);
      }
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      amount: '',
      category: 'client_payment',
      paymentMethod: 'bank_transfer',
      upiReferenceId: '',
      bankTransactionId: '',
      chequeNumber: '',
      date: new Date().toISOString().split('T')[0],
      sourceName: '',
      sourceContact: '',
      client: '',
      status: 'received',
      notes: '',
      tags: ''
    });
    setReceiptFile(null);
    setReceiptPreview(null);
    setEditingProfit(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (profit) => {
    setEditingProfit(profit);
    setForm({
      title: profit.title || '',
      description: profit.description || '',
      amount: profit.amount?.toString() || '',
      category: profit.category || 'client_payment',
      paymentMethod: profit.paymentMethod || 'bank_transfer',
      upiReferenceId: profit.upiReferenceId || '',
      bankTransactionId: profit.bankTransactionId || '',
      chequeNumber: profit.chequeNumber || '',
      date: profit.date ? new Date(profit.date).toISOString().split('T')[0] : '',
      sourceName: profit.source?.name || '',
      sourceContact: profit.source?.contact || '',
      client: profit.client?._id || '',
      status: profit.status || 'received',
      notes: profit.notes || '',
      tags: profit.tags?.join(', ') || ''
    });
    setReceiptPreview(profit.receipt?.url || null);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title.trim()) {
      setError('Title is required');
      return;
    }
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setError('Valid amount is required');
      return;
    }
    
    const formData = new FormData();
    formData.append('title', form.title.trim());
    formData.append('description', form.description.trim());
    formData.append('amount', form.amount);
    formData.append('category', form.category);
    formData.append('paymentMethod', form.paymentMethod);
    formData.append('date', form.date);
    formData.append('status', form.status);
    
    if (form.paymentMethod === 'upi' && form.upiReferenceId) {
      formData.append('upiReferenceId', form.upiReferenceId);
    }
    if (form.paymentMethod === 'bank_transfer' && form.bankTransactionId) {
      formData.append('bankTransactionId', form.bankTransactionId);
    }
    if (form.paymentMethod === 'cheque' && form.chequeNumber) {
      formData.append('chequeNumber', form.chequeNumber);
    }
    
    if (form.sourceName || form.sourceContact) {
      formData.append('source', JSON.stringify({ name: form.sourceName, contact: form.sourceContact }));
    }
    if (form.client) {
      formData.append('client', form.client);
    }
    if (form.notes) {
      formData.append('notes', form.notes);
    }
    if (form.tags) {
      formData.append('tags', JSON.stringify(form.tags.split(',').map(t => t.trim()).filter(Boolean)));
    }
    if (receiptFile) {
      formData.append('receipt', receiptFile);
    }
    
    const result = await execute(
      editingProfit 
        ? () => profitAPI.updateProfit(editingProfit._id, formData)
        : () => profitAPI.createProfit(formData)
    );
    
    if (result.success) {
      setSuccess(editingProfit ? 'Profit record updated successfully' : 'Profit record created successfully');
      setIsModalOpen(false);
      resetForm();
      fetchProfits();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to save profit record');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this profit record?')) return;
    
    const result = await execute(() => profitAPI.deleteProfit(id));
    if (result.success) {
      setSuccess('Profit record deleted successfully');
      fetchProfits();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to delete profit record');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getPaymentMethodLabel = (method) => {
    const pm = PAYMENT_METHODS.find(p => p.value === method);
    return pm ? `${pm.icon} ${pm.label}` : method;
  };

  const getCategoryLabel = (cat) => {
    const c = CATEGORIES.find(c => c.value === cat);
    return c ? c.label : cat;
  };

  const getStatusBadge = (status) => {
    const s = STATUS_OPTIONS.find(s => s.value === status);
    return s || { label: status, color: 'bg-gray-100 text-gray-700' };
  };

  return (
    <div style={{ fontFamily: FONT_UI }}>
      <PageHeader
        title="Profits"
        subtitle="Billing revenue minus expenditures, plus the income records booked by hand"
        actions={
          canManage('create') && (
            <PrimaryButton icon={Plus} onClick={openCreateModal}>
              Add profit record
            </PrimaryButton>
          )
        }
      />

      <Banner tone="error" message={error} onClose={() => setError('')} />
      <Banner tone="success" message={success} onClose={() => setSuccess('')} />

      {/* Derived P&L — revenue (invoices raised) minus platform expenditure */}
      <ProfitSummary />

      <h2 className="text-[15px] font-semibold mb-3" style={{ color: C.ink }}>
        Profit records
      </h2>
      <p className="text-[12px] mb-4" style={{ color: C.textMuted }}>
        Income booked directly against a client or source — separate from invoiced revenue above.
      </p>

      <Toolbar
        value={filters.search}
        onChange={(v) => setFilters({ ...filters, search: v })}
        placeholder="Search by title, description, source..."
      >
        <GhostButton icon={Filter} onClick={() => setShowFilters(!showFilters)}>
          Filters
        </GhostButton>
        <GhostButton
          icon={RefreshCw}
          onClick={() =>
            setFilters({ search: '', category: '', paymentMethod: '', status: '', startDate: '', endDate: '', client: '' })
          }
        >
          Reset
        </GhostButton>
      </Toolbar>

      {showFilters && (
        <Card className="p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <select
              value={filters.paymentMethod}
              onChange={(e) => setFilters({ ...filters, paymentMethod: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All payment methods</option>
              {PAYMENT_METHODS.map((pm) => (
                <option key={pm.value} value={pm.value}>{pm.label}</option>
              ))}
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink, fontFamily: FONT_DATA }}
            />
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink, fontFamily: FONT_DATA }}
            />
            <select
              value={filters.client}
              onChange={(e) => setFilters({ ...filters, client: e.target.value })}
              className="px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ border: `1px solid ${C.border}`, color: C.ink }}
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        ) : profits.length === 0 ? (
          <EmptyState
            icon={CircleDollarSign}
            title="No profit records found"
            message="Book income here when it does not come through an invoice."
            action={
              canManage('create') && (
                <PrimaryButton icon={Plus} onClick={openCreateModal}>
                  Add profit record
                </PrimaryButton>
              )
            }
          />
        ) : (
          <>
            <Table minWidth={900}>
              <thead>
                <tr>
                  <TH>Date</TH>
                  <TH>Record</TH>
                  <TH>Category</TH>
                  <TH>Paid via</TH>
                  <TH>Status</TH>
                  <TH align="right">Amount</TH>
                  <TH align="center">Receipt</TH>
                  <TH align="right">Actions</TH>
                </tr>
              </thead>
              <tbody>
                {profits.map((profit) => (
                  <tr key={profit._id} className="hover:bg-slate-50">
                    <TD>
                      <Mono className="block">{fmtDate(profit.date)}</Mono>
                    </TD>
                    <TD>
                      <span className="block font-medium">{profit.title}</span>
                      {profit.source?.name && (
                        <Mono className="block text-[11px]" style={{ color: C.textMuted }}>
                          {profit.source.name}
                        </Mono>
                      )}
                      {profit.client && (
                        <span className="block text-[11px]" style={{ color: C.emerald }}>
                          Client: {profit.client.name}
                        </span>
                      )}
                    </TD>
                    <TD>
                      <Pill tone="neutral">{getCategoryLabel(profit.category)}</Pill>
                    </TD>
                    <TD>
                      <span className="text-[12px]" style={{ color: C.textMuted }}>
                        {getPaymentMethodLabel(profit.paymentMethod)}
                      </span>
                    </TD>
                    <TD>
                      <Pill tone={PROFIT_STATUS_TONE[profit.status] || 'neutral'}>
                        {getStatusBadge(profit.status).label}
                      </Pill>
                    </TD>
                    <TD align="right">
                      <Mono className="font-semibold" style={{ color: C.emerald }}>
                        {formatCurrency(profit.amount)}
                      </Mono>
                    </TD>
                    <TD align="center">
                      {profit.receipt?.url ? (
                        <a
                          href={profit.receipt.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg"
                          style={{ background: '#ECFDF5', color: '#047857' }}
                          title="Open receipt"
                        >
                          <Image className="w-4 h-4" />
                        </a>
                      ) : (
                        <span style={{ color: C.textFaint }}>—</span>
                      )}
                    </TD>
                    <TD align="right">
                      <div className="flex justify-end gap-1">
                        <IconButton icon={Eye} title="View" onClick={() => setViewingProfit(profit)} />
                        {canManage('update') && (
                          <IconButton icon={Pencil} title="Edit" onClick={() => openEditModal(profit)} />
                        )}
                        {canManage('delete') && (
                          <IconButton icon={Trash2} title="Delete" tone={C.rose} onClick={() => handleDelete(profit._id)} />
                        )}
                      </div>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </Table>

            {pagination.pages > 1 && (
              <div
                className="flex items-center justify-between px-5 py-3"
                style={{ borderTop: `1px solid ${C.border}` }}
              >
                <Mono className="text-[12px]" style={{ color: C.textMuted }}>
                  {(pagination.page - 1) * pagination.limit + 1}–
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </Mono>
                <div className="flex gap-2">
                  <IconButton
                    icon={ChevronLeft}
                    title="Previous page"
                    onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                    disabled={pagination.page === 1}
                  />
                  <IconButton
                    icon={ChevronRight}
                    title="Next page"
                    onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    disabled={pagination.page === pagination.pages}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setIsModalOpen(false)} />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingProfit ? 'Edit Profit Record' : 'Add New Profit Record'}
                </h3>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Title & Amount */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., Monthly Service Fee - ABC Corp"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount (₹) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="number"
                        value={form.amount}
                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Category, Date & Status */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={form.category}
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      {CATEGORIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {PAYMENT_METHODS.map(pm => (
                      <button
                        key={pm.value}
                        type="button"
                        onClick={() => setForm({ ...form, paymentMethod: pm.value })}
                        className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                          form.paymentMethod === pm.value
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-xl">{pm.icon}</span>
                        <span className="text-xs mt-1">{pm.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Payment-specific fields */}
                {form.paymentMethod === 'upi' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">UPI Reference ID</label>
                    <input
                      type="text"
                      value={form.upiReferenceId}
                      onChange={(e) => setForm({ ...form, upiReferenceId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., 123456789012"
                    />
                  </div>
                )}
                {form.paymentMethod === 'bank_transfer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Transaction ID</label>
                    <input
                      type="text"
                      value={form.bankTransactionId}
                      onChange={(e) => setForm({ ...form, bankTransactionId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., TXN123456789"
                    />
                  </div>
                )}
                {form.paymentMethod === 'cheque' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cheque Number</label>
                    <input
                      type="text"
                      value={form.chequeNumber}
                      onChange={(e) => setForm({ ...form, chequeNumber: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., 000123"
                    />
                  </div>
                )}

                {/* Source Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source / Payer Name</label>
                    <input
                      type="text"
                      value={form.sourceName}
                      onChange={(e) => setForm({ ...form, sourceName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="e.g., ABC Corp, John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Source Contact</label>
                    <input
                      type="text"
                      value={form.sourceContact}
                      onChange={(e) => setForm({ ...form, sourceContact: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="Phone or Email"
                    />
                  </div>
                </div>

                {/* Client (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Link to Client (Optional)
                  </label>
                  <select
                    value={form.client}
                    onChange={(e) => setForm({ ...form, client: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">No Client</option>
                    {clients.map(c => (
                      <option key={c._id} value={c._id}>{c.name} ({c.code})</option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    rows={2}
                    placeholder="Additional details..."
                  />
                </div>

                {/* Receipt Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Receipt / Invoice
                  </label>
                  <div className="flex items-start gap-4">
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-colors"
                    >
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Click to upload receipt</p>
                      <p className="text-xs text-gray-400 mt-1">JPG, PNG or PDF (max 5MB)</p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,application/pdf"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </div>
                    {(receiptPreview || receiptFile) && (
                      <div className="relative">
                        {receiptPreview?.startsWith('data:image') || receiptPreview?.includes('.jpg') || receiptPreview?.includes('.png') ? (
                          <img src={receiptPreview} alt="Receipt" className="w-24 h-24 object-cover rounded-lg border" />
                        ) : (
                          <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-lg border">
                            <FileText className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => { setReceiptFile(null); setReceiptPreview(null); }}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Comma-separated tags (e.g., monthly, recurring, Q1)"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    rows={2}
                    placeholder="Internal notes..."
                  />
                </div>

                {/* Submit */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={actionLoading}>
                    {editingProfit ? 'Update Record' : 'Create Record'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingProfit && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black/50" onClick={() => setViewingProfit(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl max-w-lg w-full">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Profit Record Details</h3>
                <button onClick={() => setViewingProfit(null)} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900">{viewingProfit.title}</h4>
                    <p className="text-sm text-gray-500">{new Date(viewingProfit.date).toLocaleDateString('en-IN', { dateStyle: 'full' })}</p>
                  </div>
                  <span className="text-2xl font-bold text-green-600">{formatCurrency(viewingProfit.amount)}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Category</p>
                    <p className="font-medium">{getCategoryLabel(viewingProfit.category)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Payment Method</p>
                    <p className="font-medium">{getPaymentMethodLabel(viewingProfit.paymentMethod)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(viewingProfit.status).color}`}>
                      {getStatusBadge(viewingProfit.status).label}
                    </span>
                  </div>
                  {viewingProfit.upiReferenceId && (
                    <div>
                      <p className="text-gray-500">UPI Reference ID</p>
                      <p className="font-medium font-mono">{viewingProfit.upiReferenceId}</p>
                    </div>
                  )}
                  {viewingProfit.bankTransactionId && (
                    <div>
                      <p className="text-gray-500">Bank Transaction ID</p>
                      <p className="font-medium font-mono">{viewingProfit.bankTransactionId}</p>
                    </div>
                  )}
                  {viewingProfit.chequeNumber && (
                    <div>
                      <p className="text-gray-500">Cheque Number</p>
                      <p className="font-medium font-mono">{viewingProfit.chequeNumber}</p>
                    </div>
                  )}
                  {viewingProfit.source?.name && (
                    <div>
                      <p className="text-gray-500">Source</p>
                      <p className="font-medium">{viewingProfit.source.name}</p>
                      {viewingProfit.source.contact && (
                        <p className="text-xs text-gray-400">{viewingProfit.source.contact}</p>
                      )}
                    </div>
                  )}
                  {viewingProfit.client && (
                    <div>
                      <p className="text-gray-500">Client</p>
                      <p className="font-medium">{viewingProfit.client.name}</p>
                    </div>
                  )}
                </div>
                
                {viewingProfit.description && (
                  <div>
                    <p className="text-gray-500 text-sm">Description</p>
                    <p className="text-gray-700">{viewingProfit.description}</p>
                  </div>
                )}
                
                {viewingProfit.receipt?.url && (
                  <div>
                    <p className="text-gray-500 text-sm mb-2">Receipt</p>
                    <a
                      href={viewingProfit.receipt.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                    >
                      <Image className="w-4 h-4" />
                      View Receipt
                    </a>
                  </div>
                )}

                {viewingProfit.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {viewingProfit.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitsPage;
